import { addDays, differenceInDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { useInterval } from 'usehooks-ts';
import { MAX_ORDER_EXPIRY_DAYS } from '../../constants';
import { useModalState } from '../../hooks/useModalState';
import { formatDate } from '../../lib/formatters';
import { tifOptions } from '../../lib/tifOptions';
import { TimeInForce } from '../../types/orders';
import { Alert } from '../Alert';
import { Button } from '../catalyst-ui-kit/button';
import { Input } from '../Input/Input';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { useTradeFormStore } from '../TradePage/TradeForm/TradeFormState';

type GTDDateInputProps = {
  value: number | undefined;
  onChange: (days: number | undefined) => void;
};

const GTDDateInput: React.FC<GTDDateInputProps> = ({ value: days, onChange }) => {
  const [now, setNow] = useState(new Date());
  useInterval(() => {
    setNow(new Date());
  }, 1000);

  // Selected expiry date, based on inputted months and days
  const expiryDate = addDays(now, days ?? 0);

  const setMaxExpiryDays = useCallback(() => {
    onChange(MAX_ORDER_EXPIRY_DAYS);
  }, [onChange]);

  const isTooLong = useMemo(() => {
    const now = new Date();
    const expiryDate = addDays(now, days ?? 0);
    const daysDifference = differenceInDays(expiryDate, now);
    return daysDifference > MAX_ORDER_EXPIRY_DAYS;
  }, [days]);

  const hasError = !days || isTooLong;

  return (
    <>
      <div>
        <div className="flex gap-2">
          <Input
            min={0}
            step={1}
            label="Date"
            inputType="number"
            max={MAX_ORDER_EXPIRY_DAYS}
            // Allow one extra character so that max error is shown.
            maxLength={String(MAX_ORDER_EXPIRY_DAYS).length + 1}
            value={days}
            decimalScale={0}
            hasError={hasError}
            onValueChange={(values) => onChange(values.floatValue)}
            rightElement={<span className="text-xs text-contentSecondary">Day(s)</span>}
            containerClassName="flex-1"
          />
        </div>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-contentSecondary">Projected Date of Expiry</span>
        <span className="text-contentPrimary">
          {formatDate(expiryDate, {
            timeStyle: undefined,
          })}
        </span>
      </div>
      <div className={twJoin('flex flex-col gap-2', isTooLong ? 'mt-1' : 'mt-3')}>
        {isTooLong ? (
          <Alert variant="error">
            The maximum {tifOptions['GTD'].dropdownLabel} value is{' '}
            <Button text className="text-xs" onClick={setMaxExpiryDays}>
              {MAX_ORDER_EXPIRY_DAYS} days
            </Button>
            .
          </Alert>
        ) : null}
        <Button
          color="blue"
          type="submit"
          className="w-full font-semibold sm:py-2 sm:text-base"
          disabled={hasError}
        >
          Confirm
        </Button>
      </div>
    </>
  );
};

export type TimeInForceModalUiProps = {
  isOpen: boolean;
  onClose: () => void;
  currentTif: TimeInForce | undefined;
  currentExpiryInDays: number;
  onSetTif: (tif: TimeInForce) => void;
  onSetExpiryInDays: (days: number) => void;
  postOnly: boolean;
};

export const TimeInForceModalUi: React.FC<TimeInForceModalUiProps> = ({
  isOpen,
  onClose,
  currentTif,
  currentExpiryInDays,
  onSetTif,
  onSetExpiryInDays,
  postOnly,
}) => {
  const [selectedTif, setSelectedTif] = useState<TimeInForce>(currentTif || 'GTD');
  const [pendingExpiryInDays, setPendingExpiryInDays] = useState<number | undefined>(
    currentExpiryInDays
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedTif(currentTif || 'GTD');
      setPendingExpiryInDays(currentExpiryInDays || 0);
    }
  }, [isOpen, currentTif, currentExpiryInDays]);

  const handleConfirm = useCallback(() => {
    onSetTif(selectedTif);
    if (selectedTif === 'GTD') {
      onSetExpiryInDays(pendingExpiryInDays ?? 0);
    }
    onClose();
  }, [selectedTif, pendingExpiryInDays, onSetTif, onSetExpiryInDays, onClose]);

  const multiSelectOptions = Object.values(tifOptions).map((option) => ({
    value: option.value,
    label: option.dropdownLabel,
    disabled: option.disabledWithPostOnly && postOnly,
  }));

  const getTooltipText = () => {
    return selectedTif ? tifOptions[selectedTif].tooltip : '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  return (
    <ResponsiveModal onClose={onClose} open={isOpen} title="Time in Force">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <MultiSelect
          options={multiSelectOptions}
          value={selectedTif}
          onChange={(value) => setSelectedTif(value as TimeInForce)}
          className="bg-backgroundPrimary"
          multiselect={false}
          clearEnabled={false}
          showSearchInput={false}
          panelOffset={4}
        />

        <div className="text-xs font-medium text-contentSecondary">{getTooltipText()}</div>

        {selectedTif === 'GTD' ? (
          <GTDDateInput value={pendingExpiryInDays} onChange={setPendingExpiryInDays} />
        ) : (
          <Button
            color="blue"
            onClick={handleConfirm}
            className="mt-3 w-full font-semibold sm:py-2 sm:text-base"
          >
            Confirm
          </Button>
        )}
      </form>
    </ResponsiveModal>
  );
};

export const TimeInForceModal: React.FC = () => {
  const tifModal = useModalState((state) => state.tifModal);
  const setTifModal = useModalState((state) => state.setTifModal);

  const currentTif = useTradeFormStore((state) => state.tif);
  const setTif = useTradeFormStore((state) => state.setTif);
  const postOnly = useTradeFormStore((state) => state.postOnly);

  const expiryInDays = useTradeFormStore((state) => state.expiryInDays);
  const setExpiryInDays = useTradeFormStore((state) => state.setExpiryInDays);

  const handleClose = useCallback(() => {
    setTifModal('closed');
  }, [setTifModal]);

  return (
    <TimeInForceModalUi
      isOpen={tifModal === 'open'}
      onClose={handleClose}
      currentTif={currentTif}
      currentExpiryInDays={expiryInDays}
      onSetTif={setTif}
      onSetExpiryInDays={setExpiryInDays}
      postOnly={postOnly}
    />
  );
};
